<template>
    <div 
        class="benefit-content" 
        :class="{ 'benefit-locked': isLocked }"
        :style="{ backgroundImage: benefitImageUrlFromTheme ? `url(${benefitImageUrlFromTheme})` : 'none' }"
        @click="handleClick"
    >
        <!-- Benefit Badge -->
        <div class="benefit-badge">
            <span class="benefit-badge-text">BENEFIT</span>
        </div>

        <div class="benefit-container">
            <div class="benefit-header">
                <div class="benefit-icon" :class="{ 'locked': isLocked }">
                    <span 
                        class="benefit-icon-mask"
                        :class="{ 'locked': isLocked }"
                        :style="{ WebkitMaskImage: `url(${isLocked ? '/img/member-icon/locked-icon.svg' : '/img/member-icon/checklist.svg'})`, maskImage: `url(${isLocked ? '/img/member-icon/locked-icon.svg' : '/img/member-icon/checklist.svg'})` }"
                        :aria-label="isLocked ? 'Locked benefit' : 'Available benefit'"
                    ></span>
                </div>

                <div class="benefit-title-container">
                    <h3 class="benefit-title">
                        {{ benefit.name }}
                    </h3>
                    <div class="benefit-description">
                        <p class="marquee" :data-text="merchantMarqueeText">{{ merchantMarqueeText }}</p>
                    </div>
                </div>
            </div>

            <div class="benefit-dash-line"/>

            <div class="benefit-status">
                <span 
                    v-if="!isLocked" 
                    class="benefit-status-icon-mask"
                    aria-label="Available"
                ></span>
                <span 
                    v-else 
                    class="benefit-status-icon-mask locked"
                    aria-label="Locked"
                ></span>
                <span class="benefit-status-text">
                    {{ isLocked ? `Upgrade to ${displayRequiredTierName} to unlock` : 'Available for your tier' }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import { benefitImageMixin } from '@/composables/useBenefitImage';

export default {
    name: 'MemberBenefitContent',
    props: {
        benefit: {
            type: Object,
            required: true,
            default: () => ({
                name: '',
                merchant: {
                    name: ''
                },
                branch: {
                    name: ''
                },
                avatar: {
                    image_url: ''
                }
            })
        },
        isLocked: {
            type: Boolean,
            default: false
        },
        requiredTierName: {
            type: String,
            default: ''
        }
    },
    mixins: [benefitImageMixin],
    computed: {

        merchantMarqueeText() {
            const merchantName = this.benefit?.merchant?.name || '';
            const branchName = this.benefit?.branch?.name || '';

            if (merchantName && branchName) {
                return `${merchantName} - ${branchName}`;
            }

            return merchantName || branchName || 'Member Benefit';
        },
        displayRequiredTierName() {
            const name = this.requiredTierName;
            const finalName = name && name !== '-' ? name : 'Member';
            return finalName.length > 25 ? finalName.substring(0, 25) : finalName;
        }
    },
    methods: {
        handleClick() {
            this.$emit('click', this.benefit);
        }
    },
}
</script>

<style scoped>
.benefit-content {
    width: 280px;
    min-width: 280px;
    max-width: 280px;
    height: 242px;
    flex-shrink: 0;
    background-size: cover;
    background-position: top;
    background-repeat: no-repeat;
    border-radius: 12px;
    padding: 8px;
    display: flex;
    flex-direction: column;
    justify-content: end;
    position: relative;
    cursor: pointer;
    border: 1px solid #E1E4EA;
}

.benefit-content.benefit-locked {
    opacity: 0.85;
}

.benefit-badge {
    display: flex;
    padding: 6px 12px;
    background: var(--primary-bg, var(--primary-color, #4CAF50));
    border-radius: 6px 0px 0px 6px;
    position: absolute;
    top: 12px;
    right: 0px;
    z-index: 2;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.benefit-badge-text {
    font-size: 8px;
    font-weight: bold;
    color: white;
    line-height: 100%;
    letter-spacing: 0.5px;
}

.benefit-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    overflow: hidden;
}

.benefit-header {
    display: flex;
    gap: 8px;
    width: 100%;
    padding-left: 4px;
    padding-right: 4px;
}

.benefit-icon {
    width: 35px;
    min-width: 35px;
    height: 35px;
    min-height: 35px;
    background: var(--primary-bg, var(--primary-color, #4CAF50));
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.benefit-icon.locked {
    background: var(--secondary-color, #FEE2E2);
    box-shadow: none;
}

.benefit-icon-mask {
    display: inline-block;
    width: 18px;
    height: 18px;
    background-color: white;
    -webkit-mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-size: contain;
    mask-repeat: no-repeat;
    mask-position: center;
}

.benefit-icon-mask.locked {
    background-color: var(--primary-color, #F93E3E);
}

.benefit-title-container {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}

.benefit-title {
    font-size: 12px !important;
    color: black !important;
    font-weight: 520 !important;
    margin: 0 !important;
}

.benefit-description {
    font-size: 10px;
    color: #717784;
    font-weight: normal;
    margin: 0 !important;
    white-space: nowrap;
    overflow: hidden;
    width: 100%;
}

.benefit-description .marquee {
    display: inline-block;
    position: relative;
    margin: 0 !important;
    white-space: nowrap;
    animation: marquee 15s linear infinite;
    will-change: transform;
}

@keyframes marquee {
    0% { transform: translateX(0); }
    100% { transform: translateX(-100%); }
}

.benefit-description .marquee::after {
    content: attr(data-text);
    position: absolute;
    left: 100%;
    top: 0;
    white-space: nowrap;
    padding-left: 24px;
}

.benefit-dash-line {
    border: none;
    border-top: 1px dashed #E1E4EA;
    margin: 0 8px;
    position: relative;
}

.benefit-status {
    display: flex;
    gap: 8px;
    justify-content: start;
    align-items: center;
    padding-left: 4px;
    padding-right: 4px;
}

.benefit-status-text {
    font-size: 10px;
    color: var(--primary-color, #4CAF50);
    font-weight: 500;
}

.benefit-locked .benefit-status-text {
    color: var(--primary-color, #F93E3E) !important;
    font-weight: 600;
}

.benefit-status-icon-mask {
    display: inline-block;
    width: 12px;
    min-width: 12px;
    height: 12px;
    background-color: var(--primary-color, #4CAF50);
    -webkit-mask-image: url('/img/member-icon/checklist.svg');
    -webkit-mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-image: url('/img/member-icon/checklist.svg');
    mask-size: contain;
    mask-repeat: no-repeat;
    mask-position: center;
}

.benefit-status-icon-mask.locked {
    background-color: var(--primary-color, #F93E3E);
    -webkit-mask-image: url('/img/member-icon/locked-icon.svg');
    mask-image: url('/img/member-icon/locked-icon.svg');
    opacity: 0.6;
}
</style>